import { useCallback, useContext, useState } from "react";
import { useFormContext } from "react-hook-form";
import { ISO3_FIELDS } from "../../../../../data/constants";
import { getSymbolMarkerPlacement } from "../../../../../hooks/Editor/helpers";
import {
  currValidationFieldName,
  graphicsLayer,
  showpublicFieldName,
} from "../../../../../utils/API";
import {
  filterReportTags,
  getISO3List,
  isYesNoDomain,
} from "../../../../../utils/helper";
import { createSymbol } from "../../../../../utils/symbologies";
import { Checkbox } from "../../../../UI";
import { EditorContext } from "../../EditorContextProvider";
import {
  EditType,
  getFieldDefaultValue,
} from "../../EditorSwiper/EditorSwiper";
import { getFeatureIcon } from "../EditorFields";
import { FieldWrapper } from "../EditorFields-styled";
import { CustomSelect, EditorSwitch, SelectionTags } from "../Fields";
import InputField from "./Fields/InputField";
const borderColor = "#4c4c4c";

const AttributeFields = ({
  fields,
  config,
  t,
  symbology,
  onChangeHandler,
  editableLayer,
  requiredFields = [],
  showCheckbox = false,
  disabled = false,
  onInputBlur = undefined,
}) => {
  const [allowChange, setAllowChange] = useState(!disabled);
  const {
    highlightFeature,
    setHighlightSymbol,
    setHighlightFeature,
    batchUpdateFeatures,
    setBatchUpdateFeatures,
    sketchGraphicsLayer,
    activeColor,
    editType,
    step,
    addedPoints,
    publicFields,
    isReportsUpdate,
  } = useContext(EditorContext);
  const { getValues, setValue } = useFormContext();

  const renderField = (f, value) => {
    if (!highlightFeature && addedPoints.length === 0) return;
    const label = t("layer.fieldAlias." + f.name + ".title", f.alias);

    const information = t("layer.fieldAlias." + f.name + ".information", "");
    const { colorMap = {}, iconMap = {}, markerMap = {} } = symbology || {};
    const isRequired = requiredFields.includes(f.name);
    let isDisabled = !allowChange;
    const currValidationFieldValue = getValues(currValidationFieldName);
    if (
      editableLayer.layerConfig.isShownInReportManager &&
      f.name === showpublicFieldName &&
      currValidationFieldValue != 5
    ) {
      isDisabled = true;
    }

    const isPrivateField = !publicFields.includes(f.name);
    const privateFieldText = isPrivateField
      ? t("screen.message.notPublic")
      : "";

    const iso3Options = getISO3List(config);

    if (ISO3_FIELDS.includes(f.name)) {
      return (
        <CustomSelect
          field={f}
          label={label}
          value={value}
          options={iso3Options}
          menuPosition="fixed"
          menuPortalTarget={
            typeof window !== "undefined" ? document.body : null
          }
          isSearchable
          borderColor={borderColor}
          isRequired={isRequired}
          isDisabled={isDisabled}
          information={information}
          onInputBlur={onInputBlur}
          privateFieldText={privateFieldText}
        />
      );
    }
    let isReportsLayer = false;
    try {
      isReportsLayer =
        (editableLayer?.layerConfig?.extends ||
          editableLayer?.layerConfig?.alias) === "pacReports";
    } catch (error) {}

    if (f.domain) {
      const domain = f.domain;
      let codedValues = domain.codedValues;

      if (isReportsLayer) {
        codedValues = filterReportTags(domain.codedValues);
      }

      let options = codedValues.map((cv) => {
        return {
          code: cv.code,
          name: t("layer.domain." + f.name + "." + cv.code, cv.name),
        };
      });

      if (isYesNoDomain(f, editableLayer)) {
        const check = {
          yes: false,
          no: false,
          unknown: false,
        };

        options.forEach((cv) => {
          const lowerName = cv.name.toLowerCase();
          if (lowerName in check) {
            check[lowerName] = true;
          }
        });
        return (
          <div
            style={{
              margin: "4px 0px",
            }}
          >
            <EditorSwitch
              key={"i" + f.name}
              // id="editorFields"
              t={t}
              value={value}
              label={label}
              field={f}
              information={information}
              onChange={
                showpublicFieldName === f.name ? onChangeHandler : undefined
              }
              isRequired={isRequired}
              disabled={isDisabled}
              privateFieldText={privateFieldText}
              editableLayer={editableLayer}
            />
          </div>
        );
      }

      let colors = colorMap.default;
      let isColorMapField = false;
      if (Array.isArray(colorMap.fields) && colorMap.fields.includes(f.name)) {
        isColorMapField = true;
      } else if (colorMap.field === f.name) {
        isColorMapField = true;
      }

      let isIconMapField = false;
      if (Array.isArray(iconMap.fields) && iconMap.fields.includes(f.name)) {
        isIconMapField = true;
      } else if (iconMap.field === f.name) {
        isIconMapField = true;
      }

      let isMarkerMapField = false;
      if (
        Array.isArray(markerMap.fields) &&
        markerMap.fields.includes(f.name)
      ) {
        isMarkerMapField = true;
      } else if (markerMap.field === f.name) {
        isMarkerMapField = true;
      }

      const onSelect = (option) => {
        if (f.name === currValidationFieldName && option.code !== 5) {
          setValue(showpublicFieldName, 2);
        }

        if (isReportsUpdate) return;

        if (!isReportsUpdate) {
          if (isColorMapField || isIconMapField || isMarkerMapField) {
            if (
              !!highlightFeature &&
              !editableLayer.layerConfig?.isEditorCurved
            ) {
              const editGraphic = highlightFeature.clone();
              const formValues = getValues();

              Object.keys(formValues).forEach((k) => {
                editGraphic.attributes[k] = formValues[k];
              });

              const iconName = getFeatureIcon(
                editGraphic,
                symbology,
                editableLayer
              );

              let color;

              if (isColorMapField && colors) {
                color = colors[option.code];
              } else if (isIconMapField && colors) {
                let colorField = Array.isArray(colorMap.fields)
                  ? colorMap.fields[0]
                  : colorMap.field;

                color =
                  editGraphic.attributes[colorField] == null
                    ? colors["NULL"]
                    : colors[editGraphic.attributes[colorField]];
              }

              if (!color) {
                color = symbology.defaultColor;
              }

              if (color) {
                if (editableLayer.geometryType === "point") {
                  graphicsLayer.removeAll();
                } else {
                  graphicsLayer.remove(highlightFeature);
                }

                editGraphic.symbol = createSymbol(
                  symbology,
                  editableLayer,
                  iconName,
                  color ?? symbology.defaultColor,
                  symbology.defaultSize
                );

                if (sketchGraphicsLayer) {
                  const sketchGraphic = sketchGraphicsLayer.graphics.items.find(
                    (g) =>
                      g.attributes &&
                      g.attributes[editableLayer.objectIdField] ===
                        editGraphic.attributes[editableLayer.objectIdField]
                  );
                  if (sketchGraphic) {
                    sketchGraphic.attributes = editGraphic.attributes;
                    sketchGraphic.symbol = editGraphic.symbol;
                  }
                  return;
                }
                graphicsLayer.add(editGraphic);
                setHighlightSymbol(editGraphic.symbol);
                setHighlightFeature(editGraphic);
              }
            } else if (batchUpdateFeatures.length > 0) {
              const newFeatures = [...batchUpdateFeatures];

              newFeatures.forEach((item) => {
                const iconName = getFeatureIcon(
                  item.feature,
                  symbology,
                  editableLayer
                );

                let color;
                if (isColorMapField && colors) {
                  color = colors[option.code];
                } else if (isIconMapField && colors) {
                  let colorField = Array.isArray(colorMap.fields)
                    ? colorMap.fields[0]
                    : colorMap.field;
                  color =
                    item.graphic.attributes[colorField] == null
                      ? colors["NULL"]
                      : colors[item.graphic.attributes[colorField]];
                }

                if (item.graphic && color) {
                  item.graphic.symbol = createSymbol(
                    symbology,
                    editableLayer,
                    iconName,
                    color,
                    symbology.defaultSize
                  );
                }
              });

              setBatchUpdateFeatures(newFeatures);
            } else if (
              editableLayer.layerConfig?.isEditorCurved ||
              editableLayer.layerConfig.titleLabel === "roadsSituational"
            ) {
              const markerMapField = markerMap.field
                ? markerMap.field
                : Array.isArray(markerMap.fields) && markerMap.fields.length > 0
                ? markerMap.fields[0]
                : [];

              let markerPlacement = "";
              let color = colors[option.code];
              if (markerMapField === f.name) {
                markerPlacement = markerMap.default[option.code];
                color = null;
              }

              graphicsLayer.graphics.forEach((g) => {
                let placement = getSymbolMarkerPlacement({
                  isStart: g.isStart,
                  isEnd: g.isEnd,
                  markerPlacement,
                });

                const symbol = createSymbol(
                  symbology,
                  editableLayer,
                  "",
                  color ? color : g.symbol.color,
                  symbology.defaultSize,
                  placement
                );
                g.symbol = symbol;
              });
            } else if (
              editType === EditType.create &&
              editableLayer.layerConfig?.titleLabel === "roads"
            ) {
              const symbol = createSymbol(
                symbology,
                editableLayer,
                "",
                colors[option.code],
                symbology.defaultSize,
                placement
              );

              sketchGraphicsLayer.graphics.forEach((g) => {
                g.symbol = symbol;
              });
            }
          }
        }

        if (onChangeHandler) {
          onChangeHandler({ name: f.name, value: option.code });
        }
      };

      return (
        <SelectionTags
          key={"i" + f.name}
          // id="editorFields"
          label={label}
          colors={isColorMapField ? colors : {}}
          options={options}
          value={value}
          t={t}
          field={f}
          config={config}
          information={information}
          onSelect={onSelect}
          isRequired={isRequired}
          borderColor={borderColor}
          isDisabled={isDisabled}
          privateFieldText={privateFieldText}
        />
      );
    }

    switch (f.type) {
      case "double":
        return (
          <InputField
            label={label}
            type="number"
            step="any"
            defaultValue={value}
            placeholder="0.00"
            borderColor={borderColor}
            isRequired={isRequired}
            field={f}
            disabled={isDisabled}
            information={information}
            privateFieldText={privateFieldText}
          />
        );
      case "integer":
        return (
          <InputField
            label={label}
            type="number"
            step="1"
            defaultValue={value}
            placeholder="0"
            borderColor={borderColor}
            isRequired={isRequired}
            field={f}
            disabled={isDisabled}
            information={information}
            privateFieldText={privateFieldText}
          />
        );
      case "small-integer":
        return (
          <InputField
            label={label}
            type="number"
            step="1"
            defaultValue={value}
            placeholder="0"
            borderColor={borderColor}
            isRequired={isRequired}
            field={f}
            disabled={isDisabled}
            information={information}
            privateFieldText={privateFieldText}
          />
        );

      case "date":
        const today = new Date();
        const todayString = today.toISOString().split("T")[0];

        return (
          <InputField
            label={label}
            type="date"
            defaultValue={value || todayString}
            field={f}
            isRequired={isRequired}
            borderColor={borderColor}
            disabled={isDisabled}
            information={information}
            max={todayString}
            privateFieldText={privateFieldText}
          />
        );
      default:
        if (f.length > 60) {
          return (
            <InputField
              label={label}
              placeholder={label}
              maxLength={f.length}
              defaultValue={value}
              borderColor={borderColor}
              field={f}
              isRequired={isRequired}
              disabled={isDisabled}
              information={information}
              onInputBlur={onInputBlur}
              privateFieldText={privateFieldText}
            />
          );
        } else {
          return (
            <InputField
              noMargin
              label={label}
              placeholder={label}
              type="text"
              maxLength={f.length}
              defaultValue={value}
              borderColor={borderColor}
              field={f}
              isRequired={isRequired}
              disabled={isDisabled}
              information={information}
              onInputBlur={onInputBlur}
              privateFieldText={privateFieldText}
            />
          );
        }
    }
  };
  const getField = (f) => {
    let value = "";
    if (highlightFeature) {
      if (highlightFeature.attributes[f.name] === undefined) {
        value = getFieldDefaultValue(f, config);
      } else if (highlightFeature.attributes[f.name] != null) {
        if (f.type === "date")
          value = new Date(highlightFeature.attributes[f.name])
            .toISOString()
            .substring(0, 10);
        else value = highlightFeature.attributes[f.name];
      }
    }

    // if (showFilled && (value == null || value === "")) return null;

    return <FieldWrapper key={f.name}>{renderField(f, value)}</FieldWrapper>;
  };

  const onCheckboxChange = useCallback((e) => {
    const { checked } = e.target;
    setAllowChange(checked);
  }, []);

  return (
    <>
      {showCheckbox && (
        <div
          style={{
            padding: "4px 0px 8px 0px",
          }}
        >
          <Checkbox
            onChange={onCheckboxChange}
            label="Allow edit"
            color={activeColor}
            borderRadius="4px"
            checked={allowChange}
          />
        </div>
      )}
      {fields.map((field) => getField(field))}
    </>
  );
};

export default AttributeFields;
